import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Chip,
  Tooltip,
  IconButton,
  Popover,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Button,
  Divider,
} from "@material-ui/core";
import {
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";

const useStyles = makeStyles((theme) => ({
  syncChip: {
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  popoverContent: {
    padding: theme.spacing(2),
    minWidth: 300,
    maxWidth: 400,
  },
  statusItem: {
    padding: theme.spacing(1, 0),
  },
  statusIcon: {
    minWidth: 40,
  },
  syncedStatus: {
    color: theme.palette.success.main,
  },
  pendingStatus: {
    color: theme.palette.warning.main,
  },
  errorStatus: {
    color: theme.palette.error.main,
  },
  lastSyncText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  errorDetails: {
    backgroundColor: theme.palette.error.light + "20",
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    fontSize: "0.75rem",
    color: theme.palette.error.dark,
  },
  refreshButton: {
    marginTop: theme.spacing(1),
  },
}));

const SyncStatus = () => {
  const classes = useStyles();
  const { dateToClient } = useDate();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [syncStatus, setSyncStatus] = useState({
    overall: 'unknown',
    synced: 0,
    pending: 0,
    errors: 0,
    lastSync: null,
    errorDetails: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSyncStatus();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadSyncStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadSyncStatus = async () => {
    try {
      const { data } = await api.get("/catalog/sync-status");
      setSyncStatus(data);
    } catch (err) {
      console.error("Error loading sync status:", err);
    }
  };

  const handleRefreshStatus = async () => {
    setLoading(true);
    await loadSyncStatus();
    setLoading(false);
  };

  const handleChipClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getOverallStatus = () => {
    if (syncStatus.errors > 0) return 'error';
    if (syncStatus.pending > 0) return 'pending';
    if (syncStatus.synced > 0) return 'synced';
    return 'unknown';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'synced':
        return 'primary';
      case 'pending':
        return 'default';
      case 'error':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'synced':
        return <CheckCircleIcon className={classes.syncedStatus} />;
      case 'pending':
        return <ScheduleIcon className={classes.pendingStatus} />;
      case 'error':
        return <ErrorIcon className={classes.errorStatus} />;
      default:
        return <InfoIcon />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'synced':
        return 'Sincronizado';
      case 'pending':
        return 'Pendente';
      case 'error':
        return 'Erro';
      default:
        return 'Desconhecido';
    }
  };

  const overallStatus = getOverallStatus();
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Status da sincronização com WhatsApp">
        <Chip
          icon={loading ? <CircularProgress size={16} /> : getStatusIcon(overallStatus)}
          label={getStatusText(overallStatus)}
          color={getStatusColor(overallStatus)}
          onClick={handleChipClick}
          className={classes.syncChip}
          variant="outlined"
        />
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Paper className={classes.popoverContent}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6">
              Status de Sincronização
            </Typography>
            <IconButton
              size="small"
              onClick={handleRefreshStatus}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Box>

          <List dense>
            <ListItem className={classes.statusItem}>
              <ListItemIcon className={classes.statusIcon}>
                <CheckCircleIcon className={classes.syncedStatus} />
              </ListItemIcon>
              <ListItemText
                primary="Produtos Sincronizados"
                secondary={`${syncStatus.synced} produtos`}
              />
            </ListItem>

            <ListItem className={classes.statusItem}>
              <ListItemIcon className={classes.statusIcon}>
                <ScheduleIcon className={classes.pendingStatus} />
              </ListItemIcon>
              <ListItemText
                primary="Aguardando Sincronização"
                secondary={`${syncStatus.pending} produtos`}
              />
            </ListItem>

            <ListItem className={classes.statusItem}>
              <ListItemIcon className={classes.statusIcon}>
                <ErrorIcon className={classes.errorStatus} />
              </ListItemIcon>
              <ListItemText
                primary="Erros de Sincronização"
                secondary={`${syncStatus.errors} produtos`}
              />
            </ListItem>
          </List>

          {syncStatus.lastSync && (
            <>
              <Divider />
              <Typography className={classes.lastSyncText}>
                Última sincronização: {dateToClient(syncStatus.lastSync)}
              </Typography>
            </>
          )}

          {syncStatus.errorDetails && syncStatus.errorDetails.length > 0 && (
            <>
              <Divider style={{ margin: "16px 0 8px 0" }} />
              <Typography variant="subtitle2" color="error">
                Detalhes dos Erros:
              </Typography>
              {syncStatus.errorDetails.slice(0, 3).map((error, index) => (
                <Box key={index} className={classes.errorDetails}>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {error.productName}
                  </Typography>
                  <Typography variant="caption">
                    {error.error}
                  </Typography>
                </Box>
              ))}
              {syncStatus.errorDetails.length > 3 && (
                <Typography variant="caption" color="textSecondary">
                  ... e mais {syncStatus.errorDetails.length - 3} erros
                </Typography>
              )}
            </>
          )}

          {syncStatus.pending > 0 && (
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              startIcon={<SyncIcon />}
              className={classes.refreshButton}
              onClick={() => {
                // Trigger sync - this would call the sync endpoint
                api.post("/catalog/sync").then(() => {
                  handleRefreshStatus();
                }).catch(toastError);
                handleClose();
              }}
            >
              Sincronizar Agora
            </Button>
          )}
        </Paper>
      </Popover>
    </>
  );
};

export default SyncStatus;